import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtValue } from "@/lib/format";
import { ErrorState, LoadingState, PageHeader, Panel, Stat } from "@/components/dash/ui";
import { LineChart, TrendQuad } from "@/components/dash/charts";

export default function HistoricalPerformancePage() {
  const [params] = useSearchParams();
  const dims = useQuery({ queryKey: ["history-dims"], queryFn: dashApi.historyDimensions });
  const [kpi, setKpi] = useState<string>(params.get("kpi") ?? "odoo.sales_incl");
  const [days, setDays] = useState(90);
  const [company, setCompany] = useState("");
  const [branch, setBranch] = useState("");

  const c = company || undefined;
  const b = branch || undefined;
  const series = useQuery({ queryKey: ["hist-series", kpi, days, c, b], queryFn: () => dashApi.historySeries(kpi, days, c, b) });
  const trends = useQuery({ queryKey: ["hist-trends", kpi, c, b], queryFn: () => dashApi.historyTrends(kpi, c, b) });
  const fc = useQuery({ queryKey: ["hist-fc", kpi, c, b], queryFn: () => dashApi.historyForecast(kpi, 7, c, b) });
  const unit = trends.data?.unit ?? "count";

  return (
    <div className="space-y-6">
      <PageHeader title="Historical Performance" description="Deep-dive a single KPI over time, by company or branch." />

      <div className="flex flex-wrap items-center gap-2">
        <select value={kpi} onChange={(e) => setKpi(e.target.value)} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
          {(dims.data?.kpis ?? []).map((k) => <option key={k.kpi} value={k.kpi}>{k.label}</option>)}
        </select>
        <select value={company} onChange={(e) => { setCompany(e.target.value); setBranch(""); }} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
          <option value="">All companies</option>
          {(dims.data?.companies ?? []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select value={branch} onChange={(e) => { setBranch(e.target.value); setCompany(""); }} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
          <option value="">All branches</option>
          {(dims.data?.branches ?? []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
          {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}
        </select>
      </div>

      <Panel title={trends.data?.label ?? kpi} subtitle="Daily history">
        {series.isLoading ? <LoadingState /> : series.isError ? <ErrorState error={series.error} /> : (
          <LineChart points={series.data?.points ?? []} unit={unit} />
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Period comparisons" className="lg:col-span-2">
          {trends.isLoading ? <LoadingState /> : trends.data ? <TrendQuad trends={trends.data} /> : null}
        </Panel>
        <Panel title="Forecast" subtitle={fc.data?.confidence ? `${fc.data.confidence} confidence` : undefined}>
          {fc.isLoading ? <LoadingState /> : fc.data ? (
            fc.data.projected === null ? (
              <p className="py-4 text-center text-sm text-slate-500">{fc.data.method}</p>
            ) : (
              <div className="space-y-2">
                <Stat label={`Projected next ${fc.data.horizon_days}d`} value={fmtValue(fc.data.projected, unit)} />
                <p className="text-[11px] text-slate-500">slope {fc.data.slope_per_day}/day · {fc.data.points} days · {fc.data.method}</p>
              </div>
            )
          ) : null}
        </Panel>
      </div>
    </div>
  );
}
